import React, { useMemo, useRef, useState } from "react";
import ModalShell from "./modals/ModalShell";
import CourseValuePicker, {
  PickerOption,
} from "./modals/CourseValuePicker";
import NumpadPopover from "./NumpadPopover";
import ColorWheelPicker from "../../components/ColorWheelPicker";
import { Course, Hole, Tee } from "./types";
import {
  CourseSource,
  addCourse,
  upsertCourse,
  formatCourseLocation,
} from "./coursesDb";
import { submitProposal } from "./courseCatalogApi";

// A tee colour is "light" when it has too little contrast on the white
// scorecard — e.g. white or silver. Used to keep hybrid yardages legible:
// a hole borrowed from a white deck keeps the default ink colour instead of
// vanishing into the background.
function isLightColor(hex?: string): boolean {
  if (!hex) return false;
  let h = hex.replace("#", "").trim().toLowerCase();
  if (h === "white") return true;
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum > 0.7;
}

const PAR_OPTIONS = [3, 4, 5, 6];
const SI_OPTIONS = Array.from({ length: 18 }, (_, i) => i + 1);

// Par + Stroke Index stay on the flick-wheel (tiny, fixed option sets).
// Rating + Slope use the calculator numpad instead so a value like "71.2"
// or "128" can just be typed.
const PAR_PICK: PickerOption[] = PAR_OPTIONS.map((v) => ({
  value: v,
  label: String(v),
}));
const SI_PICK: PickerOption[] = SI_OPTIONS.map((v) => ({
  value: v,
  label: String(v),
}));

// Default palette for newly added tees — cycles through golf-card-like
// colours so each tee shows a distinct dot on the scorecard. Real
// imports preserve whatever colour the OCR returns or, failing that,
// fall through to this same cycle.
const TEE_PALETTE = [
  "#1d4ed8",
  "#d4a017",
  "#c0c0c0",
  "#b87333",
  "#16a34a",
  "#d63b3b",
  "#0047ab",
  "#797679",
];

interface DraftTee {
  id: string;
  name: string;
  color: string;
  slope?: number;
  rating?: number;
  // Per-hole yardage. Length 18; undefined means "not entered."
  yardages: (number | undefined)[];
  // Hybrid (combo) tee: yardages are BORROWED per hole from two other
  // decks rather than typed. `secondary[i]` false → hole i uses deck A,
  // true → deck B. The tee's own `yardages` array is unused while hybrid.
  // Materialised to a normal Tee (concrete yardages + colors +
  // secondaryHoles) at save — no schema change.
  hybrid?: { aId: string; bId: string; secondary: boolean[] };
}

interface Props {
  initialName?: string;
  initialCity?: string;
  initialRegion?: string;
  initialCountry?: string;
  // Legacy freeform location (older saved courses). Used only to prefill the
  // City box when no structured fields exist, so the value isn't lost on save.
  initialLocation?: string;
  initialHoles?: Hole[];
  initialTees?: Tee[];
  source?: CourseSource;
  title?: string;
  // When set, Save updates this existing course (by id) instead of adding
  // a new one — used by the "View card → Edit" flow.
  courseId?: string;
  // Open in read-only preview mode ("View card"). The scorecard renders
  // but nothing is tappable until the user hits "Edit card".
  readOnly?: boolean;
  // Baseline for the admin review diff: cells whose value differs from this
  // course are highlighted yellow. Used when approving an edit proposal.
  compareTo?: Course;
  // DB version this (player) edit forked from — carried on the submitted
  // proposal so admin review / the future "update available" notice can tell
  // whether the catalog moved on underneath it.
  initialBaseVersion?: number;
  // Overrides the primary Save button label (e.g. "Approve & publish" in the
  // admin review flow).
  saveLabel?: string;
  // Persistence override. When provided, Save calls this (async) instead of
  // writing to the local coursesDb — used by the CGAdmin console to write to
  // the shared backend catalog. `mode` distinguishes a new card from an edit.
  // Throwing rejects the save and surfaces the message in the modal.
  persist?: (course: Course, mode: "create" | "update") => Promise<void>;
  onSaved: () => void;
  onClose: () => void;
}

// What the tap-to-edit wheel is currently editing. Yardage + names use
// inline text inputs, and Rating/Slope use the numpad popover — so only
// Par + SI drive the wheel picker.
type PickerTarget =
  | { kind: "par"; idx: number; rect: DOMRect }
  | { kind: "si"; idx: number; rect: DOMRect };

// Stroke Index integrity. No card may repeat a number. A full 18-hole card
// must additionally use every index 1–18 exactly once; shorter cards (9-hole)
// can't fill 1–18 — they commonly carry an odd/even slice of the 18 indexes —
// so for those we only guard against duplicates. Returns an error string
// (naming the offenders) or null if valid.
function validateStrokeIndex(holes: Hole[]): string | null {
  const counts = new Map<number, number>();
  for (const h of holes) counts.set(h.si, (counts.get(h.si) || 0) + 1);
  const dups: number[] = [];
  counts.forEach((c, si) => {
    if (c > 1) dups.push(si);
  });
  if (dups.length) {
    return `Stroke Index can't repeat a number (duplicated ${dups
      .sort((a, b) => a - b)
      .join(", ")}).`;
  }
  if (holes.length === 18) {
    const missing: number[] = [];
    for (let i = 1; i <= 18; i++) if (!counts.has(i)) missing.push(i);
    const outOfRange = holes.some((h) => h.si < 1 || h.si > 18);
    if (missing.length || outOfRange) {
      const parts: string[] = [];
      if (missing.length) parts.push(`missing ${missing.join(", ")}`);
      if (outOfRange) parts.push("out of range 1–18");
      return `Stroke Index must use each number 1–18 once (${parts.join("; ")}).`;
    }
  }
  return null;
}

const defaultHoles = (): Hole[] =>
  Array.from({ length: 18 }, (_, i) => ({
    number: i + 1,
    par: 4,
    si: i + 1,
  }));

const newTeeID = () => `t_${Math.random().toString(36).slice(2, 10)}`;

const newTee = (idx: number): DraftTee => ({
  id: newTeeID(),
  name: `Tee ${idx + 1}`,
  color: TEE_PALETTE[idx % TEE_PALETTE.length],
  yardages: Array(18).fill(undefined) as (number | undefined)[],
});

function teesFromInitial(initial: Tee[] | undefined): DraftTee[] {
  if (!initial || initial.length === 0) return [];
  const drafts: DraftTee[] = initial.map((t, idx) => ({
    id: t.id || newTeeID(),
    name: t.name || `Tee ${idx + 1}`,
    color: t.color || TEE_PALETTE[idx % TEE_PALETTE.length],
    slope: t.slope,
    rating: t.rating,
    yardages: Array.from({ length: 18 }, (_, i) =>
      t.yardages && typeof t.yardages[i] === "number"
        ? t.yardages[i]
        : undefined,
    ),
  }));

  // Reconstruct hybrids saved by an earlier edit. A materialised combo tee
  // carries colors[0]/colors[1] (deck A / deck B) + secondaryHoles (holes on
  // deck B). Map those colours back to the two pure decks by colour so the
  // hybrid selectors + per-hole deck picks come back editable.
  const norm = (c?: string) => (c || "").trim().toLowerCase();
  initial.forEach((t, idx) => {
    if (!t.colors || t.colors.length < 2) return;
    const isPure = (j: number) =>
      !initial[j].colors || (initial[j].colors?.length ?? 0) < 2;
    const findParent = (col: string) =>
      drafts.find((d, j) => j !== idx && isPure(j) && norm(d.color) === norm(col));
    const a = findParent(t.colors[0]);
    const b = findParent(t.colors[1]);
    if (!a || !b) return; // parents not both present → leave as a plain tee
    const secSet = new Set(t.secondaryHoles ?? []);
    drafts[idx].hybrid = {
      aId: a.id,
      bId: b.id,
      secondary: Array.from({ length: 18 }, (_, i) => secSet.has(i + 1)),
    };
  });

  return drafts;
}

export default function CourseAddModal({
  initialName,
  initialCity,
  initialRegion,
  initialCountry,
  initialLocation,
  initialHoles,
  initialTees,
  source = "manual",
  title,
  courseId,
  readOnly = false,
  compareTo,
  initialBaseVersion,
  saveLabel,
  persist,
  onSaved,
  onClose,
}: Props) {
  const [name, setName] = useState(initialName || "");
  // Structured location. If a legacy course only has freeform `location`, seed
  // the City box with it so nothing is dropped on the next save.
  const [city, setCity] = useState(
    initialCity || (!initialRegion && !initialCountry ? initialLocation : "") || "",
  );
  const [region, setRegion] = useState(initialRegion || "");
  const [country, setCountry] = useState(initialCountry || "");
  const [holes, setHoles] = useState<Hole[]>(initialHoles || defaultHoles());
  const [tees, setTees] = useState<DraftTee[]>(() =>
    teesFromInitial(initialTees),
  );
  // Read-only preview until the user taps "Edit card". Add/import flows
  // open straight into edit mode.
  const [editing, setEditing] = useState(!readOnly);
  // Async-save state (only exercised when a `persist` handler is supplied).
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Editing UI state — the wheel picker, plus inline text edits for the
  // free-form fields (course name, tee name, per-hole yardage).
  const [picker, setPicker] = useState<PickerTarget | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [editTeeNameId, setEditTeeNameId] = useState<string | null>(null);
  const [editYard, setEditYard] = useState<{
    teeId: string;
    holeIdx: number;
  } | null>(null);

  // Par + SI updates ---------------------------------------------------

  const setPar = (i: number, par: number) => {
    setHoles((prev) =>
      prev.map((h, idx) => (idx === i ? { ...h, par } : h)),
    );
  };

  // SI auto-swap: every hole must hold a unique SI in [1..18]. When the
  // user picks a value already used by another hole, we swap the two —
  // this keeps the constraint invariant by construction (no validation
  // step at save time, no impossible states to report).
  const setSI = (i: number, si: number) => {
    setHoles((prev) => {
      const swap = prev.findIndex((h, idx) => idx !== i && h.si === si);
      const previous = prev[i].si;
      return prev.map((h, idx) => {
        if (idx === i) return { ...h, si };
        if (idx === swap) return { ...h, si: previous };
        return h;
      });
    });
  };

  // Tee CRUD -----------------------------------------------------------

  const addTee = () => setTees((prev) => [...prev, newTee(prev.length)]);

  const removeTee = (id: string) =>
    setTees((prev) => prev.filter((t) => t.id !== id));

  const updateTee = (id: string, patch: Partial<DraftTee>) =>
    setTees((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  // Only pure (non-hybrid) decks can be a hybrid's parents.
  const pureTees = tees.filter((t) => !t.hybrid);

  // Add a hybrid tee blending the first two pure decks. Every hole starts on
  // deck A; the user taps grid cells to switch a hole to deck B.
  const addHybrid = () => {
    if (pureTees.length < 2) return;
    const a = pureTees[0];
    const b = pureTees[1];
    setTees((prev) => [
      ...prev,
      {
        id: newTeeID(),
        name: `${a.name}/${b.name}`,
        color: a.color,
        yardages: Array(18).fill(undefined) as (number | undefined)[],
        hybrid: { aId: a.id, bId: b.id, secondary: Array(18).fill(false) },
      },
    ]);
  };

  const setHybridParent = (
    teeId: string,
    which: "a" | "b",
    parentId: string,
  ) =>
    setTees((prev) =>
      prev.map((t) =>
        t.id === teeId && t.hybrid
          ? {
              ...t,
              hybrid: {
                ...t.hybrid,
                [which === "a" ? "aId" : "bId"]: parentId,
              },
            }
          : t,
      ),
    );

  // Flip a hybrid hole between deck A and deck B.
  const toggleHybridSource = (teeId: string, holeIdx: number) =>
    setTees((prev) =>
      prev.map((t) => {
        if (t.id !== teeId || !t.hybrid) return t;
        const sec = t.hybrid.secondary.slice();
        sec[holeIdx] = !sec[holeIdx];
        return { ...t, hybrid: { ...t.hybrid, secondary: sec } };
      }),
    );

  // The deck a hybrid hole currently borrows from (or undefined for a pure
  // tee / missing parent).
  const hybridParent = (tee: DraftTee, i: number): DraftTee | undefined => {
    if (!tee.hybrid) return undefined;
    const useB = tee.hybrid.secondary[i];
    const id = useB ? tee.hybrid.bId : tee.hybrid.aId;
    return tees.find((t) => t.id === id);
  };

  // Displayed yardage for a hole — borrowed from the source deck for a
  // hybrid, or the tee's own typed value otherwise.
  const displayYardage = (tee: DraftTee, i: number): number | undefined => {
    if (tee.hybrid) return hybridParent(tee, i)?.yardages[i];
    return tee.yardages[i];
  };

  // Indicator dot — a solid fill for a pure tee, a split A/B fill for a
  // hybrid so it reads as a blend of its two decks.
  const dotStyle = (tee: DraftTee): React.CSSProperties => {
    if (tee.hybrid) {
      const a =
        tees.find((t) => t.id === tee.hybrid!.aId)?.color || tee.color;
      const b =
        tees.find((t) => t.id === tee.hybrid!.bId)?.color || tee.color;
      return {
        background: `linear-gradient(135deg, ${a} 0 50%, ${b} 50% 100%)`,
      };
    }
    return { background: tee.color };
  };

  // Reorder a tee to an arbitrary index. Courses list tees in a set order
  // (e.g. Blue, Cobalt/White hybrid, White) — this lets the user drag a
  // combo tee between the two it blends. The order carries through to the
  // saved course + the grid's yardage rows (both read the tees array).
  const moveTeeTo = (id: string, targetIdx: number) =>
    setTees((prev) => {
      const from = prev.findIndex((t) => t.id === id);
      if (
        from < 0 ||
        targetIdx < 0 ||
        targetIdx >= prev.length ||
        from === targetIdx
      ) {
        return prev;
      }
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(targetIdx, 0, item);
      return next;
    });

  // Drag-to-reorder via the grip handle. Pointer events unify mouse + touch;
  // touch-action:none on the grip (CSS) stops the drag from scrolling the
  // modal. As the pointer crosses into another tee's row we slot the dragged
  // tee at that index live.
  const [dragTeeId, setDragTeeId] = useState<string | null>(null);
  const dragTeeRef = useRef<string | null>(null);
  const teesRef = useRef<HTMLDivElement>(null);

  const beginTeeDrag = (id: string) => (e: React.PointerEvent) => {
    e.preventDefault();
    dragTeeRef.current = id;
    setDragTeeId(id);

    const onMove = (ev: PointerEvent) => {
      const container = teesRef.current;
      const curId = dragTeeRef.current;
      if (!container || !curId) return;
      const rows = Array.from(
        container.querySelectorAll<HTMLElement>(".cg-verify-tee"),
      );
      if (rows.length === 0) return;
      let target = rows.findIndex((r) => {
        const rect = r.getBoundingClientRect();
        return ev.clientY >= rect.top && ev.clientY <= rect.bottom;
      });
      if (target === -1) {
        target =
          ev.clientY < rows[0].getBoundingClientRect().top
            ? 0
            : rows.length - 1;
      }
      moveTeeTo(curId, target);
    };
    const onUp = () => {
      dragTeeRef.current = null;
      setDragTeeId(null);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
  };

  const setTeeYardage = (teeId: string, holeIdx: number, raw: string) => {
    const value = raw.trim() === "" ? undefined : Number(raw);
    setTees((prev) =>
      prev.map((t) => {
        if (t.id !== teeId) return t;
        const next = t.yardages.slice();
        next[holeIdx] = Number.isFinite(value as number) ? value : undefined;
        return { ...t, yardages: next };
      }),
    );
  };

  // Save ---------------------------------------------------------------

  const submit = async () => {
    setSaveError(null);
    if (!name.trim()) {
      setSaveError("Course name is required.");
      return;
    }
    const siErr = validateStrokeIndex(holes);
    if (siErr) {
      setSaveError(siErr);
      return;
    }
    const finalTees: Tee[] | undefined = tees.length
      ? tees.map((t) => {
          // A hybrid materialises to a normal combo Tee: concrete per-hole
          // yardages borrowed from its two decks, plus colors[] +
          // secondaryHoles[] so the round card paints each hole in the
          // source deck's colour. No hybrid metadata is persisted.
          if (t.hybrid) {
            const a = tees.find((x) => x.id === t.hybrid!.aId);
            const b = tees.find((x) => x.id === t.hybrid!.bId);
            const per = holes.map((_, i) => {
              const src = t.hybrid!.secondary[i] ? b : a;
              const y = src?.yardages[i];
              return typeof y === "number" ? y : undefined;
            });
            const filled = per.some((y) => typeof y === "number");
            const secondaryHoles = t.hybrid.secondary
              .map((s, i) => (s ? i + 1 : 0))
              .filter((n) => n > 0);
            return {
              id: t.id,
              name: t.name.trim() || "Tee",
              color: a?.color || t.color,
              colors: [a?.color || t.color, b?.color || t.color],
              slope: t.slope,
              rating: t.rating,
              yardages: filled
                ? per.map((y) => (typeof y === "number" ? y : 0))
                : undefined,
              secondaryHoles: secondaryHoles.length ? secondaryHoles : undefined,
            };
          }
          const filled = t.yardages.some((y) => typeof y === "number");
          // Yardages serialize as number[] only when at least one is
          // present — otherwise drop the field so the scorecard's
          // hasYardage check stays clean.
          const yardages = filled
            ? t.yardages.map((y) => (typeof y === "number" ? y : 0))
            : undefined;
          return {
            id: t.id,
            name: t.name.trim() || "Tee",
            color: t.color,
            slope: t.slope,
            rating: t.rating,
            yardages,
          };
        })
      : undefined;
    // Persistence override (CGAdmin → shared backend catalog). Async; a thrown
    // error keeps the modal open with the message shown.
    if (persist) {
      const assembled: Course = {
        id: courseId || "",
        name,
        city,
        region,
        country,
        holes,
        tees: finalTees,
      };
      setSaving(true);
      setSaveError(null);
      try {
        await persist(assembled, courseId ? "update" : "create");
      } catch (err) {
        setSaveError((err as Error).message || "Failed to save course");
        setSaving(false);
        return;
      }
      setSaving(false);
      onSaved();
      return;
    }

    // Player (local) save. Persists to their browser, and also submits the
    // change to admin for review — their local copy is used immediately; the
    // proposal decides whether it goes global. Proposal submission is
    // best-effort: a failure must not fail the local save.
    if (courseId) {
      // Editing an existing course — keep its id so it updates in place.
      upsertCourse(
        { id: courseId, name, city, region, country, holes, tees: finalTees },
        source,
      );
      submitProposal({
        kind: "edit",
        courseId,
        course: { id: courseId, name, city, region, country, holes, tees: finalTees },
        baseVersion: initialBaseVersion,
      }).catch((e) =>
        console.warn("[caddiegames] proposal submit failed:", e),
      );
    } else {
      const rec = addCourse({
        name,
        city,
        region,
        country,
        holes,
        tees: finalTees,
        source,
      });
      submitProposal({
        kind: "new",
        courseId: rec.id,
        course: { id: rec.id, name, city, region, country, holes, tees: finalTees },
      }).catch((e) =>
        console.warn("[caddiegames] proposal submit failed:", e),
      );
    }
    onSaved();
  };

  // Cancel out of edit mode. For a card opened via "View card" (read-only),
  // this discards the unsaved edits and returns to the scorecard preview
  // rather than closing the whole modal back to the list. For the add/import
  // flows (which open straight into edit mode) there's no preview to return
  // to, so it closes.
  const cancelEdit = () => {
    if (!readOnly) {
      onClose();
      return;
    }
    setName(initialName || "");
    setCity(
      initialCity ||
        (!initialRegion && !initialCountry ? initialLocation : "") ||
        "",
    );
    setRegion(initialRegion || "");
    setCountry(initialCountry || "");
    setHoles(initialHoles || defaultHoles());
    setTees(teesFromInitial(initialTees));
    setEditingName(false);
    setPicker(null);
    setEditTeeNameId(null);
    setEditYard(null);
    setEditing(false);
  };

  // Grid geometry — mirror the round scorecard: front 9 · Out · back 9 ·
  // In · Tot, with the same column variables so the two cards read alike.
  const front = holes.slice(0, 9);
  const back = holes.slice(9);
  const hasBack = back.length > 0;
  const parOut = front.reduce((s, h) => s + h.par, 0);
  const parIn = back.reduce((s, h) => s + h.par, 0);
  const totalPar = parOut + parIn;
  // CSS `repeat(0, …)` is invalid and collapses the whole grid into a single
  // column (making a 9-hole card render as a vertical list). When there's no
  // back nine, drop the Out/In split and render name + holes + one Tot column.
  const gridCols = hasBack
    ? `var(--cg-name-col) repeat(${front.length}, minmax(var(--cg-hole-col), 1fr)) var(--cg-total-col) repeat(${back.length}, minmax(var(--cg-hole-col), 1fr)) var(--cg-total-col) var(--cg-total-col)`
    : `var(--cg-name-col) repeat(${front.length}, minmax(var(--cg-hole-col), 1fr)) var(--cg-total-col)`;

  const yardSum = (tee: DraftTee, from: number, to: number) => {
    let sum = 0;
    for (let i = from; i < to; i++) sum += displayYardage(tee, i) || 0;
    return sum;
  };

  // Admin review diff — when a baseline (compareTo) is supplied, cells whose
  // value differs get a yellow wash so the reviewer sees exactly what changed.
  const cmpTeeById = useMemo(
    () => new Map((compareTo?.tees || []).map((t) => [t.id, t])),
    [compareTo],
  );
  const parChanged = (i: number) =>
    !!compareTo && compareTo.holes?.[i]?.par !== holes[i]?.par;
  const siChanged = (i: number) =>
    !!compareTo && compareTo.holes?.[i]?.si !== holes[i]?.si;
  const yardChanged = (tee: DraftTee, i: number) => {
    if (!compareTo) return false;
    const was = cmpTeeById.get(tee.id)?.yardages?.[i];
    const now = displayYardage(tee, i);
    return (was ?? null) !== (now ?? null);
  };
  const ratingChanged = (tee: DraftTee) =>
    !!compareTo &&
    (cmpTeeById.get(tee.id)?.rating ?? null) !== (tee.rating ?? null);
  const slopeChanged = (tee: DraftTee) =>
    !!compareTo &&
    (cmpTeeById.get(tee.id)?.slope ?? null) !== (tee.slope ?? null);
  const nameChanged = !!compareTo && (compareTo.name || "") !== name;
  const chg = (on: boolean) => (on ? " cg-verify-changed" : "");

  // Resolve the active wheel picker into concrete options + handlers.
  const pickerConfig = (() => {
    if (!picker) return null;
    if (picker.kind === "par") {
      return {
        options: PAR_PICK,
        value: holes[picker.idx]?.par,
        label: `Par · Hole ${holes[picker.idx]?.number}`,
        onPick: (v: number) => setPar(picker.idx, v),
      };
    }
    return {
      options: SI_PICK,
      value: holes[picker.idx]?.si,
      label: `SI · Hole ${holes[picker.idx]?.number}`,
      onPick: (v: number) => setSI(picker.idx, v),
    };
  })();

  const openPicker = (target: PickerTarget) => (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPicker({ ...target, rect } as PickerTarget);
  };

  // Inline yardage editor — rendered inside the tapped cell so the native
  // numeric keyboard handles the free-form value. For a hybrid the cell
  // isn't typed; it shows the borrowed yardage in the source deck's colour
  // and taps flip decks (handled by the cell's onClick), so bail early.
  const yardCell = (tee: DraftTee, i: number) => {
    if (tee.hybrid) {
      const parent = hybridParent(tee, i);
      // Colour the number in the source deck's colour — but not when that
      // colour is white/too light, or it would vanish on the white cell.
      const tint =
        parent && !isLightColor(parent.color) ? parent.color : undefined;
      return (
        <span
          className="cg-verify-cell-val"
          style={tint ? { color: tint } : undefined}
        >
          {displayYardage(tee, i) ?? ""}
        </span>
      );
    }
    const cellEditing =
      editYard?.teeId === tee.id && editYard.holeIdx === i;
    if (cellEditing) {
      return (
        <input
          className="cg-verify-cell-input"
          type="number"
          inputMode="numeric"
          min={0}
          max={900}
          autoFocus
          defaultValue={tee.yardages[i] ?? ""}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={(e) => {
            setTeeYardage(tee.id, i, e.currentTarget.value);
            setEditYard(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") setEditYard(null);
          }}
        />
      );
    }
    return (
      <span className="cg-verify-cell-val">
        {tee.yardages[i] ?? ""}
      </span>
    );
  };

  return (
    <ModalShell
      title={title || "Add course"}
      className="cg-modal-verify"
      onClose={onClose}
      footer={
        editing ? (
          <>
            {saveError && (
              <span className="cg-verify-save-error">{saveError}</span>
            )}
            <button
              type="button"
              className="cg-btn cg-btn-ghost"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cg-btn cg-btn-primary"
              onClick={submit}
              disabled={!name.trim() || saving}
            >
              {saving
                ? "Saving…"
                : saveLabel
                  ? saveLabel
                  : courseId
                    ? "Save changes"
                    : "Save course"}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="cg-btn cg-btn-ghost"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className="cg-btn cg-btn-primary"
              onClick={() => setEditing(true)}
            >
              Edit card
            </button>
          </>
        )
      }
    >
      <div className="cg-verify">
        <p className="cg-verify-intro">
          {editing
            ? "Tap any value to change it."
            : "Scorecard preview — tap Edit card to make changes."}
        </p>

        {/* Course name — tap to edit, like the round card's title. */}
        {!editing ? (
          <div className="cg-verify-coursename cg-verify-coursename-static">
            {name.trim() || "Untitled course"}
          </div>
        ) : editingName ? (
          <input
            className="cg-verify-coursename-input"
            type="text"
            value={name}
            autoFocus
            placeholder="Course name"
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setEditingName(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Escape")
                setEditingName(false);
            }}
          />
        ) : (
          <button
            type="button"
            className={`cg-verify-coursename${
              name.trim() ? "" : " cg-verify-blank"
            }${chg(nameChanged)}`}
            onClick={() => setEditingName(true)}
          >
            {name.trim() || "Tap to name this course"}
          </button>
        )}

        {/* Location — split into City / Region / Country so the catalog can
            filter by region or country. Read-only preview shows the formatted
            line; edit mode shows the three labelled boxes. */}
        {!editing ? (
          formatCourseLocation({ city, region, country }) ? (
            <div className="cg-verify-courseloc cg-verify-courseloc-static">
              {formatCourseLocation({ city, region, country })}
            </div>
          ) : null
        ) : (
          <div className="cg-verify-loc-row">
            <label className="cg-verify-loc-field cg-verify-loc-city">
              <span>City / Town</span>
              <input
                type="text"
                value={city}
                placeholder="e.g. Innisfil"
                onChange={(e) => setCity(e.target.value)}
              />
            </label>
            <label className="cg-verify-loc-field cg-verify-loc-region">
              <span>Region</span>
              <input
                type="text"
                value={region}
                placeholder="e.g. ON"
                onChange={(e) => setRegion(e.target.value)}
              />
            </label>
            <label className="cg-verify-loc-field cg-verify-loc-country">
              <span>Country</span>
              <input
                type="text"
                value={country}
                placeholder="e.g. Canada"
                onChange={(e) => setCountry(e.target.value)}
              />
            </label>
          </div>
        )}

        {/* Scorecard grid — reuses the round card's layout + cells. */}
        <div className="cg-scrollwrap cg-verify-scroll">
          <div className="cg-grid" style={{ gridTemplateColumns: gridCols }}>
            {/* Hole numbers */}
            <div className="cg-cell cg-row-head cg-col-name cg-sticky-left">
              Hole
            </div>
            {front.map((h) => (
              <div key={`hn-${h.number}`} className="cg-cell cg-row-head">
                {h.number}
              </div>
            ))}
            {hasBack && (
              <>
                <div className="cg-cell cg-row-head cg-total-col cg-out-col">
                  Out
                </div>
                {back.map((h) => (
                  <div key={`hn-${h.number}`} className="cg-cell cg-row-head">
                    {h.number}
                  </div>
                ))}
                <div className="cg-cell cg-row-head cg-total-col">In</div>
              </>
            )}
            <div className="cg-cell cg-row-head cg-total-col">Tot</div>

            {/* Par (tap to edit) */}
            <div className="cg-cell cg-row-info cg-col-name cg-sticky-left">
              Par
            </div>
            {front.map((h, i) => (
              <div
                key={`par-${h.number}`}
                className={`cg-cell cg-row-info${
                  editing ? " cg-verify-edit" : ""
                }${chg(parChanged(i))}`}
                onClick={
                  editing
                    ? openPicker({ kind: "par", idx: i, rect: {} as DOMRect })
                    : undefined
                }
              >
                {h.par}
              </div>
            ))}
            {hasBack && (
              <>
                <div className="cg-cell cg-row-info cg-total-col cg-out-col">
                  {parOut}
                </div>
                {back.map((h, i) => (
                  <div
                    key={`par-${h.number}`}
                    className={`cg-cell cg-row-info${
                      editing ? " cg-verify-edit" : ""
                    }${chg(parChanged(i + front.length))}`}
                    onClick={
                      editing
                        ? openPicker({
                            kind: "par",
                            idx: i + front.length,
                            rect: {} as DOMRect,
                          })
                        : undefined
                    }
                  >
                    {h.par}
                  </div>
                ))}
                <div className="cg-cell cg-row-info cg-total-col">{parIn}</div>
              </>
            )}
            <div className="cg-cell cg-row-info cg-total-col">{totalPar}</div>

            {/* Stroke Index (tap to edit) */}
            <div className="cg-cell cg-row-info cg-col-name cg-sticky-left">
              SI
            </div>
            {front.map((h, i) => (
              <div
                key={`si-${h.number}`}
                className={`cg-cell cg-row-info cg-si${
                  editing ? " cg-verify-edit" : ""
                }${chg(siChanged(i))}`}
                onClick={
                  editing
                    ? openPicker({ kind: "si", idx: i, rect: {} as DOMRect })
                    : undefined
                }
              >
                {h.si}
              </div>
            ))}
            {hasBack && (
              <>
                <div className="cg-cell cg-row-info cg-total-col cg-out-col" />
                {back.map((h, i) => (
                  <div
                    key={`si-${h.number}`}
                    className={`cg-cell cg-row-info cg-si${
                      editing ? " cg-verify-edit" : ""
                    }${chg(siChanged(i + front.length))}`}
                    onClick={
                      editing
                        ? openPicker({
                            kind: "si",
                            idx: i + front.length,
                            rect: {} as DOMRect,
                          })
                        : undefined
                    }
                  >
                    {h.si}
                  </div>
                ))}
                <div className="cg-cell cg-row-info cg-total-col" />
              </>
            )}
            <div className="cg-cell cg-row-info cg-total-col" />

            {/* One yardage row per tee (tap a cell to edit) */}
            {tees.map((tee) => (
              <React.Fragment key={`yd-${tee.id}`}>
                <div className="cg-cell cg-row-info cg-col-name cg-sticky-left cg-verify-teelabel">
                  <span
                    className="cg-verify-dot"
                    style={dotStyle(tee)}
                    aria-hidden
                  />
                  <span className="cg-verify-teelabel-name">{tee.name}</span>
                </div>
                {front.map((h, i) => (
                  <div
                    key={`yd-${tee.id}-${h.number}`}
                    className={`cg-cell cg-row-info cg-yards${
                      editing ? " cg-verify-edit" : ""
                    }${chg(yardChanged(tee, i))}`}
                    onClick={
                      !editing
                        ? undefined
                        : () =>
                            tee.hybrid
                              ? toggleHybridSource(tee.id, i)
                              : setEditYard({ teeId: tee.id, holeIdx: i })
                    }
                  >
                    {yardCell(tee, i)}
                  </div>
                ))}
                {hasBack && (
                  <>
                    <div className="cg-cell cg-row-info cg-total-col cg-out-col cg-yards">
                      {yardSum(tee, 0, 9) || ""}
                    </div>
                    {back.map((h, i) => (
                      <div
                        key={`yd-${tee.id}-${h.number}`}
                        className={`cg-cell cg-row-info cg-yards${
                          editing ? " cg-verify-edit" : ""
                        }${chg(yardChanged(tee, i + front.length))}`}
                        onClick={
                          !editing
                            ? undefined
                            : () =>
                                tee.hybrid
                                  ? toggleHybridSource(tee.id, i + front.length)
                                  : setEditYard({
                                      teeId: tee.id,
                                      holeIdx: i + front.length,
                                    })
                        }
                      >
                        {yardCell(tee, i + front.length)}
                      </div>
                    ))}
                    <div className="cg-cell cg-row-info cg-total-col cg-yards">
                      {yardSum(tee, 9, 18) || ""}
                    </div>
                  </>
                )}
                <div className="cg-cell cg-row-info cg-total-col cg-yards">
                  {yardSum(tee, 0, 18) || ""}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Tees — compact editable chips (name · rating · slope). The
            yardages themselves live in the grid above; this strip carries
            the per-tee identity + course-handicap numbers. */}
        <div className="cg-verify-tees" ref={teesRef}>
          <div className="cg-verify-tees-head">
            <span className="cg-edit-label">Tees</span>
            {editing && (
              <div className="cg-verify-tees-add">
                <button
                  type="button"
                  className="cg-btn cg-btn-ghost cg-tee-add"
                  onClick={addTee}
                >
                  + Add tee
                </button>
                <button
                  type="button"
                  className="cg-btn cg-btn-ghost cg-tee-add"
                  onClick={addHybrid}
                  disabled={pureTees.length < 2}
                  title={
                    pureTees.length < 2
                      ? "Add two tees first to blend a hybrid"
                      : "Blend two decks into a hybrid tee"
                  }
                >
                  + Add hybrid
                </button>
              </div>
            )}
          </div>

          {tees.length === 0 && (
            <p className="cg-subpage-note">
              {editing
                ? "No tees found. Add one with rating & slope to enable course-handicap math, or save without."
                : "No tees recorded for this course."}
            </p>
          )}

          {tees.map((tee) => (
            <div
              key={tee.id}
              className={`cg-verify-tee${
                dragTeeId === tee.id ? " cg-verify-tee-dragging" : ""
              }`}
            >
              {editing && (
                <span
                  className="cg-verify-grip"
                  role="button"
                  tabIndex={-1}
                  aria-label={`Drag to reorder ${tee.name}`}
                  title="Drag to reorder"
                  onPointerDown={beginTeeDrag(tee.id)}
                >
                  ⋮
                </span>
              )}
              {editing && !tee.hybrid ? (
                <span className="cg-verify-swatch">
                  <ColorWheelPicker
                    value={tee.color}
                    onChange={(hex) => updateTee(tee.id, { color: hex })}
                    ariaLabel={`Colour for ${tee.name}`}
                  />
                </span>
              ) : (
                <span
                  className="cg-verify-dot"
                  style={dotStyle(tee)}
                  aria-hidden
                />
              )}
              {!editing ? (
                <span className="cg-verify-tee-name cg-verify-tee-name-static">
                  {tee.name}
                </span>
              ) : editTeeNameId === tee.id ? (
                <input
                  className="cg-verify-tee-name-input"
                  type="text"
                  autoFocus
                  defaultValue={tee.name}
                  placeholder="Tee name"
                  onBlur={(e) => {
                    updateTee(tee.id, {
                      name: e.currentTarget.value.trim() || tee.name,
                    });
                    setEditTeeNameId(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                    if (e.key === "Escape") setEditTeeNameId(null);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="cg-verify-tee-name"
                  onClick={() => setEditTeeNameId(tee.id)}
                >
                  {tee.name}
                </button>
              )}

              {tee.hybrid &&
                (editing ? (
                  <span className="cg-verify-blend">
                    <select
                      className="cg-verify-blend-select"
                      value={tee.hybrid.aId}
                      onChange={(e) =>
                        setHybridParent(tee.id, "a", e.target.value)
                      }
                      aria-label="Deck A"
                    >
                      {pureTees.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <span className="cg-verify-blend-plus">+</span>
                    <select
                      className="cg-verify-blend-select"
                      value={tee.hybrid.bId}
                      onChange={(e) =>
                        setHybridParent(tee.id, "b", e.target.value)
                      }
                      aria-label="Deck B"
                    >
                      {pureTees.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </span>
                ) : (
                  <span className="cg-verify-blend cg-verify-blend-static">
                    {tees.find((t) => t.id === tee.hybrid!.aId)?.name ?? "?"} +{" "}
                    {tees.find((t) => t.id === tee.hybrid!.bId)?.name ?? "?"}
                  </span>
                ))}

              <span className={`cg-verify-chip${chg(ratingChanged(tee))}`}>
                <span className="cg-verify-chip-k">CR</span>
                {editing ? (
                  <NumpadPopover
                    className="cg-verify-chip-num"
                    value={tee.rating}
                    onChange={(v) => updateTee(tee.id, { rating: v })}
                    min={20}
                    max={95}
                    maxDecimals={1}
                    displayDecimals={1}
                    maxLength={4}
                    placeholder="—"
                    ariaLabel={`Course rating for ${tee.name}`}
                  />
                ) : (
                  <span className="cg-verify-chip-v">
                    {tee.rating != null ? tee.rating.toFixed(1) : "—"}
                  </span>
                )}
              </span>
              <span className={`cg-verify-chip${chg(slopeChanged(tee))}`}>
                <span className="cg-verify-chip-k">Slope</span>
                {editing ? (
                  <NumpadPopover
                    className="cg-verify-chip-num"
                    value={tee.slope}
                    onChange={(v) => updateTee(tee.id, { slope: v })}
                    min={55}
                    max={155}
                    maxDecimals={1}
                    maxLength={5}
                    placeholder="—"
                    ariaLabel={`Slope for ${tee.name}`}
                  />
                ) : (
                  <span className="cg-verify-chip-v">{tee.slope ?? "—"}</span>
                )}
              </span>

              {editing && (
                <button
                  type="button"
                  className="cg-verify-tee-remove"
                  onClick={() => removeTee(tee.id)}
                  aria-label={`Remove ${tee.name}`}
                  title="Remove tee"
                >
                  ×
                </button>
              )}

              {editing && tee.hybrid && (
                <p className="cg-verify-blend-hint">
                  Tap a yardage above to switch that hole between the two
                  decks — its colour shows which it's borrowing from.
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {picker && pickerConfig && (
        <CourseValuePicker
          options={pickerConfig.options}
          value={pickerConfig.value}
          label={pickerConfig.label}
          anchorRect={picker.rect}
          onPick={pickerConfig.onPick}
          onClose={() => setPicker(null)}
        />
      )}
    </ModalShell>
  );
}
